import { ProductRoutes } from "@components/Data/enums";

// Hashing function
export const hashString = async (message: string): Promise<string> => {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hashHex;
};

// Regex patterns
export const nameRegex = /^[a-zA-Z. ]+$/;
export const phoneRegex = /([0-9])\1{6,}/;
export const validPhoneRegex = (min: number, max: number): RegExp =>
    new RegExp(`^\\d{${min},${max}}$`);

// Button ID utilities
export const getNextButtonId = (idx: string): string => {
    switch (idx) {
        case "0":
            return "start";
        case "1":
            return "phonenumber-next";
        case "2":
            return "name-next";
        case "3":
            return "dob-next";
        case "4":
            return "office-health-confirmation-next";
        default:
            return "start";
    }
};

export const getBackButtonId = (idx: string): string => {
    switch (idx) {
        case "2":
            return "name-back-button";
        case "3":
            return "dob-back-button";
        case "4":
            return "office-health-confirmation-back-button";
        default:
            return "back-button";
    }
};

// Product route constraints
export const constraints = {
    [ProductRoutes.BHARAT]: { max: 66, min: 18 },
    [ProductRoutes.REASSUREV2]: { max: 100, min: 18 },
    [ProductRoutes.REASSURE]: { max: 100, min: 18 },
    [ProductRoutes.SENIOR_FIRST]: { max: 76, min: 61 },
    [ProductRoutes.ASPIRE]: { max: 66, min: 18 },
    [ProductRoutes.HEALTH_RECHARGE]: { max: 66, min: 18 },
    [ProductRoutes.ASPIRE_SAVER]: { max: 66, min: 18 },
    [ProductRoutes.HEALTH_PREMIA]: { max: 66, min: 18 },
    [ProductRoutes.GO_ACTIVE]: { max: 66, min: 18 },
    [ProductRoutes.HEARTBEAT]: { max: 66, min: 18 },
    [ProductRoutes.HEALTH_PULSE]: { max: 66, min: 18 },
    default: { max: 100, min: 18 },
};

// Survey options
export const surveyOptions = {
    primary: ["Yes", "No"],
    cover: [
        "Less than 3 lakhs",
        "Rs 3 Lakhs - Rs 5 lakh",
        "More than Rs 5 lakh",
        "Don't Remember",
    ],
};

// SVG Icons
interface IconProps {
    id?: string;
    size?: number | string;
}

interface IconProps {
    id?: string;
    size?: number | string;
}

export const LuChevronRight = ({ id = "", size = 24 }: IconProps): string => `
    <svg
        stroke="currentColor"
        fill="none"
        stroke-width="2"
        viewBox="0 0 24 24"
        stroke-linecap="round"
        stroke-linejoin="round"
        id="${id}"
        width="${size}"
        height="${size}"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path id="${id}" d="m9 18 6-6-6-6"></path>
    </svg>
`;

export const LuChevronLeft = ({ id = "", size = 24 }: IconProps): string => `
    <svg
        stroke="currentColor"
        fill="none"
        stroke-width="2"
        viewBox="0 0 24 24"
        stroke-linecap="round"
        stroke-linejoin="round"
        id="${id}"
        width="${size}"
        height="34"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path id="${id}" d="m15 18-6-6 6-6"></path>
    </svg>
`;

/**
  * Helper function to generate unique IDs for form elements.
  * @param suffix - The specific identifier for the element.
  * @returns A unique string ID with the formName prefix.
  */
export const generateId = (suffix: string, formName: string) => `${formName}-${suffix}`;
